import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import FooterSocialLinks from './FooterSocialLinks';
import IMAGES from '../assets/images';
import { categoryPath } from '../utils/categoryUrl';

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { to: '/',        label: t('home') },
    { to: '/products',label: t('allProducts') },
    { to: '/about',   label: t('ourStory') },
    { to: '/contact', label: t('contact') },
    { to: '/support', label: t('support') },
  ];

  const categoryLinks = [
    { to: categoryPath('tatlılar'),      label: t('footerCategoryDesserts') },
    { to: categoryPath('hamur-isleri'),  label: t('footerCategoryPastries') },
    { to: categoryPath('ana-yemekler'),  label: t('footerCategoryMains') },
    { to: categoryPath('dondurma-pasta'),label: t('footerCategoryIceCream') },
    { to: categoryPath('bakkaliye'),     label: t('footerCategoryGrocery') },
  ];

  const legalLinks = [
    { to: '/privacy-policy',           label: t('privacyPolicy') },
    { to: '/distance-sales-agreement', label: t('distanceSalesAgreement') },
    { to: '/cookie-policy',            label: t('cookiePolicy') },
    { to: '/terms-of-use',             label: t('termsOfUse') },
  ];

  return (
    <footer className="bg-stone-950 text-stone-400">

      {/* ── Newsletter ── */}
      <div className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row items-center justify-between gap-10">
          <div className="max-w-md text-center lg:text-left">
            <span className="section-label text-amber-400 block mb-3">{t('worldOfPrivileges')}</span>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-white mb-3 leading-snug">
              {t('newsletterTitleLine1')}{' '}
              <span className="text-amber-400">{t('newsletterTitleHighlight')}</span>
            </h2>
            <p className="text-sm text-stone-500 leading-relaxed">
              {t('newsletterDescriptionFooter')}
            </p>
          </div>

          <div className="w-full max-w-md shrink-0">
            <form
              className="flex gap-2"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder={t('enterEmailAddress')}
                className="flex-1 bg-stone-900 border border-stone-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-stone-100 placeholder:text-stone-600 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors"
              />
              <button
                type="submit"
                className="btn-primary px-5 py-2.5 rounded-xl text-sm shrink-0"
              >
                {t('subscribe')}
              </button>
            </form>
            <p className="text-xs text-stone-600 mt-2.5 pl-1">
              {t('unsubscribeNote')}
            </p>
          </div>
        </div>
      </div>

      {/* ── Ana Footer ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Marka */}
          <div className="space-y-5">
            <Link to="/" className="inline-block">
              <span className="text-xl font-display font-bold text-white tracking-tight">
                E-TİCARET
              </span>
            </Link>
            <p className="text-sm text-stone-500 leading-relaxed">
              {t('footerBrandDescription')}
            </p>
            <div>
              <p className="text-xs font-semibold text-stone-300 uppercase tracking-wider mb-3">
                {t('followUs')}
              </p>
              <FooterSocialLinks />
            </div>
          </div>

          {/* Hızlı Linkler */}
          <FooterLinkList title={t('quickLinks')} links={quickLinks} />

          {/* Kategoriler */}
          <FooterLinkList title={t('footerFavoriteFlavors')} links={categoryLinks} />

          {/* İletişim */}
          <div>
            <FooterSectionTitle>{t('contact')}</FooterSectionTitle>
            <div className="space-y-4">
              <ContactItem icon="fa-location-dot" label={t('headOffice')}>
                Anadolu Yolu No:42,<br />Kortrijk, Belçika / 8500
              </ContactItem>
              <ContactItem icon="fa-phone" label={t('customerLine')}>
                +32 412 34 56 78
              </ContactItem>
              <ContactItem icon="fa-envelope" label={t('email')}>
                info@eticaret.com
              </ContactItem>
            </div>
          </div>
        </div>
      </div>

      {/* ── Alt Bar ── */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stone-600">
            © {new Date().getFullYear()} Eticaret. {t('allRightsReserved')}
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {legalLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-xs text-stone-600 hover:text-amber-400 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {IMAGES?.payment?.bancontact && (
              <img src={IMAGES.payment.bancontact} className="h-4 w-auto opacity-60" alt="Bancontact" />
            )}
            {IMAGES?.payment?.mastercard && (
              <img src={IMAGES.payment.mastercard} className="h-5 w-auto opacity-60" alt="Mastercard" />
            )}
            {IMAGES?.payment?.visa && (
              <img src={IMAGES.payment.visa} className="h-5 w-auto opacity-60" alt="Visa" />
            )}
            {IMAGES?.payment?.stripe && (
              <img src={IMAGES.payment.stripe} className="h-5 w-auto opacity-60" alt="Stripe" />
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ── Yardımcı Alt Bileşenler ── */

const FooterSectionTitle = ({ children }) => (
  <h4 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
    {children}
    <span className="flex-1 h-px bg-white/8" />
  </h4>
);

const FooterLinkList = ({ title, links }) => (
  <div>
    <FooterSectionTitle>{title}</FooterSectionTitle>
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.to}>
          <Link
            to={link.to}
            className="text-sm text-stone-500 hover:text-stone-200 transition-colors duration-200 flex items-center gap-2 group"
          >
            <i className="fas fa-chevron-right text-[9px] text-amber-600 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const ContactItem = ({ icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0 mt-0.5">
      <i className={`fas ${icon} text-amber-500 text-xs`} />
    </div>
    <div>
      <p className="text-xs font-semibold text-stone-300 mb-0.5">{label}</p>
      <p className="text-xs text-stone-500 leading-relaxed">{children}</p>
    </div>
  </div>
);

export default Footer;
